import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import type { CatalogItem } from '@peles/shared';
import { InlineEditButton } from '../common/InlineEditButton';

export interface ItemAutosuggestProps {
  /** Free-text item name shown in the input. */
  value: string;
  /** Catalog items available for the chosen category. */
  items: CatalogItem[];
  disabled?: boolean;
  /** Called with the next (name, catalogItemId). catalogItemId is null for free text. */
  onChange: (name: string, catalogItemId: string | null) => void;
  /** When provided, offers a "create catalog item" row for unmatched text. */
  onCreateItem?: (name: string) => void;
  /** Currently-selected catalog item id (null/empty for free text). */
  selectedId?: string | null;
  /**
   * When provided and a catalog item is selected, an inline "Edit" affordance
   * opens the item's quick-edit view. Selecting an item never opens it on its own.
   */
  onEditItem?: (catalogItemId: string) => void;
}

/**
 * Combobox for the plan-row item: type to search the catalog (within the chosen
 * category), auto-detect free text when nothing matches, and optionally create a
 * new catalog item from the typed text. Mirrors VendorAutosuggest.
 */
export function ItemAutosuggest({
  value,
  items,
  disabled,
  onChange,
  onCreateItem,
  selectedId,
  onEditItem,
}: ItemAutosuggestProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredItems = useMemo(() => {
    if (value.length < 1) return items;
    const lower = value.toLowerCase();
    return items.filter((i) => i.name.toLowerCase().includes(lower));
  }, [items, value]);

  // An exact (case-insensitive) match means the typed text already names an
  // existing catalog item, so the "create" row is suppressed.
  const hasExactMatch = useMemo(
    () => items.some((i) => i.name.trim().toLowerCase() === value.trim().toLowerCase()),
    [items, value],
  );
  const canCreate = !!onCreateItem && value.trim().length > 0 && !hasExactMatch;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value, null);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleSelect = useCallback(
    (item: CatalogItem) => {
      onChange(item.name, item.id);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;
      const totalItems = filteredItems.length + (canCreate ? 1 : 0);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          handleSelect(filteredItems[highlightedIndex]);
        } else if (highlightedIndex === filteredItems.length && canCreate) {
          onCreateItem!(value.trim());
          setIsOpen(false);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    },
    [isOpen, filteredItems, highlightedIndex, handleSelect, canCreate, onCreateItem, value],
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const lis = dropdownRef.current.querySelectorAll('li');
      const li = lis[highlightedIndex];
      if (li && typeof li.scrollIntoView === 'function') li.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const showDropdown = isOpen && !disabled && (filteredItems.length > 0 || canCreate);

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={disabled ? 'Select a category first' : 'Search catalog or type a new item'}
        aria-label="Item"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls="item-autosuggest-list"
        autoComplete="off"
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
      />

      {showDropdown && (
        <ul
          id="item-autosuggest-list"
          ref={dropdownRef}
          role="listbox"
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredItems.map((item, index) => (
            <li
              key={item.id}
              role="option"
              aria-selected={highlightedIndex === index}
              className={`px-3 py-2 cursor-pointer text-sm ${
                highlightedIndex === index
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-gray-50 text-gray-900'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {item.name}
            </li>
          ))}
          {canCreate && (
            <li
              role="option"
              aria-selected={highlightedIndex === filteredItems.length}
              className={`px-3 py-2 cursor-pointer text-sm border-t border-gray-100 ${
                highlightedIndex === filteredItems.length
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-primary-600 hover:bg-gray-50'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onCreateItem!(value.trim());
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(filteredItems.length)}
            >
              + Create item &ldquo;{value.trim()}&rdquo;
            </li>
          )}
        </ul>
      )}

      {selectedId && onEditItem && (
        <div className="mt-1 flex">
          <InlineEditButton
            onClick={() => onEditItem(selectedId)}
            label="Edit item"
            text="Edit"
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}
